import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DISTRICTS, IMAGES } from '../mock/data';
import { Reveal, Eyebrow } from '../components/xc/Bits';
import { InvestorNote } from '../components/xc/InvestorNote';
import HeroVideo from '../components/xc/HeroVideo';
import { X, Lock, Users, MapPin, Target, Building2, ShoppingBag, User, ChevronRight, Radio } from 'lucide-react';

function DistrictPanel({ district, onClose }) {
  const nav = useNavigate();
  if (!district) return null;
  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md xc-glass border-l border-[#B026FF]/25 h-full overflow-y-auto no-scrollbar xc-fade-up">
        <div className="relative h-48">
          <img src={IMAGES.neonCity} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#151119] to-transparent" />
          <button onClick={onClose} className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-md bg-black/50 border border-white/10"><X className="h-4 w-4 text-white" /></button>
          <div className="absolute bottom-4 left-5">
            <span className="text-xs tracking-[0.24em] uppercase" style={{ color: district.locked ? '#B026FF' : '#FF204E' }}>{district.tag}</span>
            <h3 className="font-display font-700 text-2xl text-white">{district.name}</h3>
          </div>
        </div>
        <div className="p-6">
          <p className="text-white/60 text-sm leading-relaxed">{district.desc}</p>
          {district.locked ? (
            <div className="mt-5 rounded-xl p-4 border border-[#B026FF]/30 bg-[#B026FF]/8 flex items-center gap-3">
              <Lock className="h-5 w-5 text-[#B026FF]" />
              <span className="text-sm text-white/80">Unlocks at Level {district.unlockLevel}</span>
            </div>
          ) : (
            <div className="mt-5 flex items-center gap-2 text-sm text-[#2FBF71]"><Radio className="h-4 w-4" /> {district.players} players present</div>
          )}
          <div className="mt-6">
            <div className="text-xs tracking-[0.2em] uppercase text-white/40 mb-3">Activities</div>
            <div className="space-y-2">
              {district.activities.map((a, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg px-4 py-3 bg-white/[0.03] border border-white/8">
                  <span className="text-sm text-white/80">{a}</span>
                  <ChevronRight className="h-4 w-4 text-white/30" />
                </div>
              ))}
            </div>
          </div>
          {!district.locked && district.current && (
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button onClick={() => nav('/missions')} className="btn-neon rounded-lg py-3 text-sm font-600">View Missions</button>
              <button onClick={() => nav('/casino')} className="btn-ghost rounded-lg py-3 text-sm font-600">Enter Casino</button>
            </div>
          )}
          {!district.locked && district.playable && (
            <button onClick={() => nav(`/district/${district.id}`)} className="mt-6 btn-neon rounded-lg w-full py-3.5 text-sm font-700 flex items-center justify-center gap-2">
              Enter {district.name} <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function City() {
  const nav = useNavigate();
  const [selected, setSelected] = useState(null);
  const quick = [
    { icon: Target, label: 'Missions', to: '/missions', color: '#FF204E' },
    { icon: Users, label: 'Crew', to: '/crew', color: '#B026FF' },
    { icon: ShoppingBag, label: 'Shop', to: '/shop', color: '#E0B85A' },
    { icon: User, label: 'Profile', to: '/profile', color: '#B026FF' },
  ];
  return (
    <div className="bg-[#070609] min-h-screen">
      {/* Cinematic city header */}
      <section className="relative h-[56vh] min-h-[420px] overflow-hidden">
        <HeroVideo
          poster={IMAGES.foggyCity}
          src720="/hero/city-720.mp4"
          src360="/hero/city-360.mp4"
          objectPosition="center"
        />
        <div className="absolute inset-0 city-overlay" />
        <div className="absolute inset-0 xc-grid opacity-30" />
        <div className="absolute inset-0 vignette" />
        <div className="relative max-w-[1440px] mx-auto px-4 lg:px-8 h-full flex flex-col justify-end pb-10">
          <Eyebrow color="#B026FF">City Overview</Eyebrow>
          <h1 className="font-display font-700 text-3xl sm:text-4xl md:text-6xl text-white tracking-tight xc-fade-up">XINCITY<sup className="text-[0.45em] align-super text-white/60">™</sup> <span className="text-gradient-rp">AFTER DARK</span></h1>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-[#2FBF71]"><Radio className="h-4 w-4 xc-pulse" /> 43 players online</div>
            <div className="flex items-center gap-2 text-sm text-white/60"><MapPin className="h-4 w-4 text-[#FF204E]" /> Current district: Downtown</div>
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-14">
        <div className="flex items-end justify-between mb-8">
          <div><Eyebrow>Districts</Eyebrow><h2 className="font-display font-700 text-2xl md:text-3xl text-white">Choose where the night takes you</h2>
            <div className="mt-3"><InvestorNote id="districts" title="Why districts gate content">
              Districts pace the world like game levels: early access feels generous, locked districts create aspiration. Progression gating extends lifetime engagement without touching game odds — status, not stakes, is the reward.
            </InvestorNote></div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {DISTRICTS.map((d, i) => (
            <Reveal key={d.id} delay={i * 60}>
              <button onClick={() => setSelected(d)} className="xc-card rounded-2xl overflow-hidden text-left w-full group relative">
                <div className="relative h-44">
                  <img src={[IMAGES.neonCity, IMAGES.rainyStreet, IMAGES.purpleCity, IMAGES.wetStreet, IMAGES.skyline, IMAGES.penthouse2][i % 6]} alt={d.name} className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 ${d.locked ? 'grayscale opacity-50' : ''}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B10] via-[#0D0B10]/30 to-transparent" />
                  {d.locked && <div className="absolute inset-0 grid place-items-center"><div className="flex flex-col items-center gap-2"><Lock className="h-7 w-7 text-[#B026FF]" /><span className="text-xs text-white/80">Level {d.unlockLevel}</span></div></div>}
                  {d.current && <span className="absolute top-3 left-3 text-[10px] font-600 tracking-[0.16em] uppercase px-2.5 py-1 rounded-full text-white" style={{ background: '#D90035' }}>You are here</span>}
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-700 text-lg text-white">{d.name}</h3>
                    <span className="text-[11px] tracking-[0.16em] uppercase" style={{ color: d.locked ? '#B026FF' : '#FF204E' }}>{d.tag}</span>
                  </div>
                  <p className="mt-2 text-sm text-white/50 line-clamp-2">{d.desc}</p>
                  {!d.locked && <div className="mt-3 flex items-center gap-1.5 text-xs text-white/45"><Users className="h-3.5 w-3.5" /> {d.players} present</div>}
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        {/* Quick links */}
        <div className="mt-12">
          <Eyebrow color="#E0B85A">Quick access</Eyebrow>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
            {quick.map((q, i) => (
              <button key={i} onClick={() => nav(q.to)} className="xc-card rounded-xl p-5 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg grid place-items-center" style={{ background: `${q.color}16`, border: `1px solid ${q.color}40` }}><q.icon className="h-5 w-5" style={{ color: q.color }} /></div>
                <span className="font-500 text-white">{q.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <DistrictPanel district={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
